<script>
import { extend } from "vee-validate";
import logo from "@/mixins/logo";
import userService from "@/services/user";
import { SELF_SIGNUP_ENABLED } from "@/services/constants";

/**
 * @description Tipos de documento ofrecidos en el registro (subconjunto del
 *   enum `governmentId.type` del User en la API).
 */
export const DOCUMENT_TYPES = [
  { value: "dni", label: "DNI" },
  { value: "lc", label: "Libreta Cívica" },
  { value: "le", label: "Libreta de Enrolamiento" },
  { value: "ci", label: "Cédula de Identidad" },
  { value: "pas", label: "Pasaporte" },
];

/** @description Sexo según el enum `gender` del User (m/f/x). */
export const GENDERS = [
  { value: "f", label: "Femenino" },
  { value: "m", label: "Masculino" },
  { value: "x", label: "X / No binario" },
];

/** @description Declaración de cobertura (pedido del Municipio de Campana). */
export const COVERAGE_OPTIONS = [
  { value: "publica", label: "Cobertura Pública" },
  { value: "pami", label: "PAMI" },
  { value: "ioma", label: "IOMA" },
  { value: "ooss", label: "Prepagas / Obras Sociales" },
];

/** @description Lado máximo (px) al que se reduce la foto del DNI antes de enviarla. */
export const DNI_IMAGE_MAX_SIDE = 1600;

/**
 * @description Texto del pop-up de domicilio no actualizado. PLACEHOLDER
 *   hasta que Prensa del Municipio defina el definitivo (debe enumerar los
 *   beneficios de tener el domicilio en Campana).
 */
export const RESIDENCE_DIALOG_TEXT =
  "Para completar tu registro en Campana Salud necesitás tener el domicilio de tu DNI actualizado en el partido de Campana. " +
  "Actualizá tu domicilio en el Registro Civil o en RENAPER y volvé a registrarte con tu DNI nuevo. " +
  "Tener el domicilio en Campana te permite acceder a los turnos, trámites y beneficios de salud del Municipio.";

/**
 * @description Valida un CUIL/CUIT: 11 dígitos, dígito verificador módulo 11
 *   y, si se pasa el DNI, que los dígitos 3..10 coincidan con el DNI
 *   (relleno con ceros a 8). Espejo de `validateCuil` de la API.
 * @param {string} cuil - con o sin guiones
 * @param {string} [dni]
 * @returns {boolean}
 */
export function isValidCuil(cuil, dni) {
  const digits = String(cuil || "").replace(/\D/g, "");
  if (digits.length !== 11) return false;
  const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  const sum = weights.reduce((acc, w, i) => acc + w * Number(digits[i]), 0);
  const mod = sum % 11;
  const expected = mod === 0 ? 0 : mod === 1 ? 9 : 11 - mod;
  const check = Number(digits[10]);
  const validCheck =
    mod === 1 ? check === 9 || check === 4 : check === expected;
  if (!validCheck) return false;
  if (dni) {
    const dniDigits = String(dni).replace(/\D/g, "").padStart(8, "0");
    if (digits.slice(2, 10) !== dniDigits) return false;
  }
  return true;
}

/** @description Prefijos posibles de un CUIL/CUIT de persona física. */
export const CUIL_PREFIXES = ["20", "23", "24", "27"];

/** @description Prefijo histórico por sexo (previo a la RG Conjunta AFIP/ANSES 5007/2021). */
const CUIL_PREFIX_BY_GENDER = { m: "20", f: "27" };

/**
 * @description Dígito verificador (módulo 11, pesos AFIP) de los 10 primeros
 *   dígitos de un CUIL. Devuelve `null` cuando el resto es 1: para ese
 *   prefijo no existe verificador válido.
 * @param {string} tenDigits - prefijo (2) + DNI (8)
 * @returns {string|null}
 */
function cuilCheckDigit(tenDigits) {
  const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  const sum = weights.reduce((acc, w, i) => acc + w * Number(tenDigits[i]), 0);
  const mod = sum % 11;
  if (mod === 0) return "0";
  if (mod === 1) return null;
  return String(11 - mod);
}

/**
 * @description UC-REG-308: arma el CUIL a partir del DNI. El DNI va fijo en
 *   el medio (rellenado con ceros a 8). El prefijo se sugiere por sexo
 *   (20 masculino / 27 femenino) o se usa el elegido a mano; el verificador
 *   se calcula SIEMPRE (módulo 11). Si el prefijo sugerido da resto 1 se pasa
 *   al 23, como asigna la AFIP (queda 9 para hombres y 4 para mujeres). Si el
 *   prefijo elegido a mano da resto 1 se usa 9/4, que es lo que acepta
 *   `isValidCuil` (y `validateCuil` de la API).
 *   No es infalible: desde la RG Conjunta 5007/2021 los CUIL nuevos llevan
 *   prefijo aleatorio (20/23/24/27) y hay casos históricos con 23/24 por DNI
 *   duplicado; por eso el prefijo queda editable en el formulario.
 * @param {string} dni - 7 u 8 dígitos (se ignoran puntos/espacios)
 * @param {string} gender - m / f / x
 * @param {string} [prefix] - prefijo elegido a mano (20/23/24/27)
 * @returns {{prefix: string, dni: string, check: string}|null}
 */
export function computeCuil(dni, gender, prefix) {
  const dniDigits = String(dni || "").replace(/\D/g, "");
  if (dniDigits.length < 7 || dniDigits.length > 8) return null;
  const dni8 = dniDigits.padStart(8, "0");
  const chosen = prefix ? String(prefix) : "";
  let p = chosen || CUIL_PREFIX_BY_GENDER[gender] || "";
  if (!p) return null;
  let check = cuilCheckDigit(p + dni8);
  if (check === null) {
    if (chosen) {
      check = gender === "f" ? "4" : "9";
    } else {
      p = "23";
      check = cuilCheckDigit(p + dni8);
    }
  }
  return { prefix: p, dni: dni8, check };
}

// Regla de vee-validate registrada al cargar este chunk (solo lo carga el
// build de Campana, ver router/public.js). `cuil:@dni` cruza con el campo
// DNI del mismo formulario.
extend("cuil", {
  params: ["dni"],
  validate: (value, { dni }) => isValidCuil(value, dni),
  message: "El CUIL/CUIT no es válido o no corresponde al DNI ingresado.",
});

/**
 * @description Reduce una imagen a `DNI_IMAGE_MAX_SIDE` px de lado y la
 *   convierte a JPEG base64 (sin prefijo data:). Menos peso para subir desde
 *   el celular y para el proveedor de lectura. Si el browser no puede
 *   decodificarla, devuelve el archivo original en base64.
 * @param {File} file
 * @returns {Promise<{data: string, mimeType: string}>}
 */
export function resizeImageToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("No se pudo leer la imagen."));
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      const original = {
        data: dataUrl.replace(/^data:[^;]+;base64,/, ""),
        mimeType: file.type || "image/jpeg",
      };
      if (typeof Image === "undefined" || typeof document === "undefined") {
        return resolve(original);
      }
      const img = new Image();
      img.onerror = () => resolve(original);
      img.onload = () => {
        try {
          const scale = Math.min(
            1,
            DNI_IMAGE_MAX_SIDE / Math.max(img.width, img.height)
          );
          const canvas = document.createElement("canvas");
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          const ctx = canvas.getContext("2d");
          if (!ctx) return resolve(original);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const jpeg = canvas.toDataURL("image/jpeg", 0.85);
          resolve({
            data: jpeg.replace(/^data:[^;]+;base64,/, ""),
            mimeType: "image/jpeg",
          });
        } catch (e) {
          resolve(original);
        }
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });
}

/** @description Códigos de error de la API que este form muestra por su cuenta (sin el toast genérico). */
const HANDLED_ERRORS = {
  DniDataMismatch: null,
  ResidenceNotLocal: null,
  DniImageUnreadable: null,
  IdentityCheckUnavailable:
    "La verificación de identidad no está disponible en este momento. Intentá de nuevo más tarde.",
  DniScanFailed:
    "No pudimos leer la foto de tu DNI. Sacala de nuevo con buena luz, sin reflejos, y volvé a intentar.",
  InvalidCuil: "El CUIL/CUIT no es válido o no corresponde al DNI ingresado.",
  MissingIdentityFields: "Completá todos los campos obligatorios.",
  InvalidDniImage: "La foto del DNI tiene que ser una imagen JPG o PNG.",
};

/** @description Nombres de los campos que pueden no coincidir con el DNI, para el aviso. */
const FIELD_LABELS = {
  dni: "número de DNI",
  firstName: "nombre",
  lastName: "apellido",
  bornAt: "fecha de nacimiento",
  gender: "sexo",
};

/**
 * @component SelfSignUpCampana
 * @description UC-REG-254 / UC-REG-291 — Portal de autoregistro del tenant
 *              Campana. Instancia PROPIA (no comparte código con
 *              `SelfSignUp.vue`, el form de Corporación Médica): decisión
 *              explícita de Diego (2026-10-08). Se monta en `/signup` solo
 *              cuando el build es `THEME=campana` (ver `router/public.js`).
 *
 *              Backend: `POST /users/self-signup` con el motor `open`. El
 *              backend crea el User paciente y manda el mail/WhatsApp con el
 *              link para definir la contraseña. No autologuea.
 *
 *              UC-REG-291 (`VUE_APP_SELF_SIGNUP_IDENTITY_CHECK=true`): suma
 *              CUIL/CUIT, tipo de documento, sexo y cobertura obligatorios y
 *              la foto del DNI. La API lee la foto con IA y corrobora que
 *              los datos cargados sean los del DNI; si el domicilio del DNI
 *              no está en el partido de Campana, se muestra un pop-up
 *              invitando a actualizarlo (el registro no se completa). La foto
 *              se reduce en el browser y NO se guarda en ningún lado.
 *              Con el flag apagado el formulario es el de UC-REG-254.
 *
 *              UC-REG-308: el CUIL/CUIT no se tipea: se arma solo con el DNI
 *              (fijo, en el medio), un prefijo sugerido por sexo y editable
 *              (20/23/24/27) y el dígito verificador calculado. Ver
 *              `computeCuil`. Cambiar DNI o sexo vuelve a sugerir el prefijo;
 *              cambiar el prefijo recalcula el verificador.
 * @example <SelfSignUpCampana />
 */
export default {
  name: "SelfSignUpCampana",

  mixins: [logo],

  data() {
    return {
      dni: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      birthDate: null,
      cuilPrefix: "",
      documentType: "dni",
      gender: "",
      coverage: "",
      dniImage: null,
      dniImageName: "",
      dniImageError: "",
      mismatchFields: [],
      residenceDialogVisible: false,
      isLoading: false,
      isEnabled: SELF_SIGNUP_ENABLED,
      identityCheck: process.env.VUE_APP_SELF_SIGNUP_IDENTITY_CHECK === "true",
      documentTypes: DOCUMENT_TYPES,
      genders: GENDERS,
      cuilPrefixes: CUIL_PREFIXES,
      coverageOptions: COVERAGE_OPTIONS,
      residenceDialogText: RESIDENCE_DIALOG_TEXT,
    };
  },

  computed: {
    /**
     * @description UC-REG-308: parte fija del CUIL = DNI rellenado a 8
     *              dígitos ("" si el DNI todavía no tiene 7 u 8 dígitos).
     * @returns {string}
     */
    cuilDni() {
      const digits = String(this.dni || "").replace(/\D/g, "");
      if (digits.length < 7 || digits.length > 8) return "";
      return digits.padStart(8, "0");
    },

    /**
     * @description UC-REG-308: dígito verificador para el prefijo elegido y
     *              el DNI cargado ("" si falta alguno).
     * @returns {string}
     */
    cuilCheck() {
      if (!this.cuilPrefix || !this.cuilDni) return "";
      const built = computeCuil(this.dni, this.gender, this.cuilPrefix);
      return built ? built.check : "";
    },

    /**
     * @description CUIL/CUIT completo (11 dígitos) que viaja al backend; ""
     *              mientras falte el prefijo o el DNI (dispara `required`).
     * @returns {string}
     */
    cuil() {
      if (!this.cuilPrefix || !this.cuilDni || !this.cuilCheck) return "";
      return `${this.cuilPrefix}${this.cuilDni}${this.cuilCheck}`;
    },

    /**
     * @description Texto del aviso cuando los datos no coinciden con el DNI.
     * @returns {string}
     */
    mismatchMessage() {
      if (!this.mismatchFields.length) return "";
      const labels = this.mismatchFields
        .map((f) => FIELD_LABELS[f] || f)
        .join(", ");
      return `Los datos que cargaste no coinciden con los de la foto del DNI: ${labels}. Revisalos y volvé a intentar.`;
    },
  },

  watch: {
    // UC-REG-308: DNI o sexo nuevos → se vuelve a sugerir el prefijo (pisa
    // una elección manual previa, porque pertenecía a otro DNI/sexo).
    dni() {
      this.suggestCuilPrefix();
    },
    gender() {
      this.suggestCuilPrefix();
    },
  },

  created() {
    if (!this.isEnabled) {
      this.$router.replace({ name: "login" });
    }
  },

  methods: {
    /**
     * @description UC-REG-308: sugiere el prefijo del CUIL por sexo para el
     *              DNI cargado (vacío si no hay DNI válido o el sexo es X).
     */
    suggestCuilPrefix() {
      const built = computeCuil(this.dni, this.gender);
      this.cuilPrefix = built ? built.prefix : "";
    },

    /**
     * @description Payload que viaja al backend. Separado de `signUp` para
     *              poder testearlo sin red. Con el flag de identidad suma los
     *              campos ampliados y la foto.
     * @returns {Object}
     */
    buildPayload() {
      const payload = {
        dni: String(this.dni || "").trim(),
        bornAt: this.birthDate,
        email: String(this.email || "")
          .trim()
          .toLowerCase(),
        firstName: String(this.firstName || "").trim(),
        lastName: String(this.lastName || "").trim(),
        phone: String(this.phone || "").trim(),
      };
      if (this.identityCheck) {
        payload.cuil = String(this.cuil || "").replace(/\D/g, "");
        payload.governmentIdType = this.documentType;
        payload.gender = this.gender;
        payload.coverage = this.coverage;
        payload.dniImage = this.dniImage;
      }
      return payload;
    },

    /**
     * @description Toma la foto elegida/sacada, la reduce y la guarda en
     *              base64 para el payload. Nunca sube el archivo a un bucket.
     * @param {Event} event - change del input file
     * @returns {Promise<void>}
     */
    async onDniImageChange(event) {
      const file =
        event && event.target && event.target.files && event.target.files[0];
      this.dniImageError = "";
      this.mismatchFields = [];
      if (!file) {
        this.dniImage = null;
        this.dniImageName = "";
        return;
      }
      if (!/^image\//.test(file.type || "")) {
        this.dniImage = null;
        this.dniImageName = "";
        this.dniImageError = "Elegí una foto (JPG o PNG).";
        return;
      }
      try {
        this.dniImage = await resizeImageToBase64(file);
        this.dniImageName = file.name;
      } catch (e) {
        this.dniImage = null;
        this.dniImageName = "";
        this.dniImageError = "No pudimos procesar la foto. Probá con otra.";
      }
    },

    /**
     * @description Mensaje para el interceptor de `services/api.js`: null
     *              para los códigos que este form resuelve con su propia UI
     *              (sin toast), texto propio para los que van por toast, y
     *              undefined para dejar el mensaje genérico.
     * @param {Object} err - error de axios
     * @returns {string|null|undefined}
     */
    getErrorMessage(err) {
      const code =
        err && err.response && err.response.data && err.response.data.error;
      if (code && Object.prototype.hasOwnProperty.call(HANDLED_ERRORS, code)) {
        return HANDLED_ERRORS[code];
      }
      return undefined;
    },

    /**
     * @description Reacciona a los errores de verificación con UI propia:
     *              campos que no coinciden (aviso inline), domicilio fuera
     *              de Campana (pop-up) y foto ilegible (error en el campo).
     * @param {Object} err - error de axios
     */
    handleSignupError(err) {
      const data = (err && err.response && err.response.data) || {};
      if (data.error === "DniDataMismatch") {
        this.mismatchFields = Array.isArray(data.fields) ? data.fields : [];
      } else if (data.error === "ResidenceNotLocal") {
        this.residenceDialogVisible = true;
      } else if (data.error === "DniImageUnreadable") {
        this.dniImageError =
          "No pudimos leer tu DNI en la foto. Sacala de frente, con buena luz y sin reflejos.";
        this.dniImage = null;
        this.dniImageName = "";
      }
    },

    /**
     * @description Envía el registro. Éxito → aviso de que llega el mail y
     *              redirect a login. Error → UI propia para los códigos de
     *              verificación; el resto lo muestra el interceptor (toast).
     * @returns {Promise<void>}
     */
    signUp() {
      if (this.identityCheck && !this.dniImage) {
        this.dniImageError =
          "Sacale una foto al frente de tu DNI para continuar.";
        return Promise.resolve();
      }
      this.isLoading = true;
      this.mismatchFields = [];
      const payload = this.buildPayload();

      return userService
        .selfSignup(payload, { getErrorMessage: this.getErrorMessage })
        .then(() => {
          this.$message.success(
            `¡Listo! Te enviamos un mail a ${payload.email} para activar tu cuenta y definir tu contraseña.`
          );
          this.$router.push({ name: "login" });
        })
        .catch((err) => this.handleSignupError(err))
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<template lang="pug">
  .login(v-if="isEnabled")
    .login__container
      ValidationObserver(v-slot="{handleSubmit}")
        form.login__card(@submit.prevent="handleSubmit(signUp)")
          .login__img
            img.login__svg(v-if="logoLoginUrl || logoAltUrl" :src="logoLoginUrl || logoAltUrl" alt="Logo")
            micon.login__svg(v-else :name="'quantux-logo-horizontal-nuevo'")
          h2.signup__title Registrate
          p.signup__hint Completá tus datos. Te vamos a enviar un mail para activar tu cuenta y definir tu contraseña.
          ValidationProvider(v-if="identityCheck" name="Tipo de documento" rules="required" v-slot="{ errors }")
            el-select#documentType.signup__select(v-model="documentType" placeholder="Tipo de documento")
              el-option(v-for="t in documentTypes" :key="t.value" :label="t.label" :value="t.value")
            span.has-error {{ errors[0] }}
          ValidationProvider(name="DNI" vid="dni" rules="required|numeric|min:7|max:8" v-slot="{ errors }")
            el-input#dni(placeholder="Número de documento (sin puntos)" v-model="dni" autocapitalize="none" inputmode="numeric")
            span.has-error {{ errors[0] }}
          .signup__cuil(v-if="identityCheck")
            label.signup__cuil-label(for="cuilPrefix") CUIL/CUIT
            .signup__cuil-parts
              el-select#cuilPrefix.signup__cuil-prefix(v-model="cuilPrefix" placeholder="Prefijo")
                el-option(v-for="p in cuilPrefixes" :key="p" :label="p" :value="p")
              span.signup__cuil-sep -
              el-input#cuilDni.signup__cuil-dni(:value="cuilDni" placeholder="DNI" readonly tabindex="-1")
              span.signup__cuil-sep -
              el-input#cuilCheck.signup__cuil-check(:value="cuilCheck" placeholder="?" readonly tabindex="-1")
            p.signup__cuil-hint Lo armamos con tu DNI y tu sexo. Si en tu CUIL figura otro prefijo (23 o 24), elegilo: el último dígito se recalcula solo.
            ValidationProvider(name="CUIL/CUIT" rules="required|cuil:@dni" v-slot="{ errors }")
              input#cuil(type="hidden" :value="cuil")
              span.has-error {{ errors[0] }}
          ValidationProvider(name="Nombre" rules="required" v-slot="{ errors }")
            el-input#firstName(placeholder="Nombre" v-model="firstName")
            span.has-error {{ errors[0] }}
          ValidationProvider(name="Apellido" rules="required" v-slot="{ errors }")
            el-input#lastName(placeholder="Apellido" v-model="lastName")
            span.has-error {{ errors[0] }}
          ValidationProvider(name="Fecha de nacimiento" rules="required" v-slot="{ errors }")
            el-date-picker.login__date(
              v-model="birthDate"
              type="date"
              placeholder="Fecha de nacimiento"
              format="dd/MM/yyyy"
              value-format="yyyy-MM-dd"
            )
            span.has-error {{ errors[0] }}
          ValidationProvider(v-if="identityCheck" name="Sexo" rules="required" v-slot="{ errors }")
            el-select#gender.signup__select(v-model="gender" placeholder="Sexo (como figura en tu DNI)")
              el-option(v-for="g in genders" :key="g.value" :label="g.label" :value="g.value")
            span.has-error {{ errors[0] }}
          ValidationProvider(name="Email" rules="required|email" v-slot="{ errors }")
            el-input#email(placeholder="Email" v-model="email" autocapitalize="none" inputmode="email")
            span.has-error {{ errors[0] }}
          ValidationProvider(name="Teléfono" v-slot="{ errors }")
            el-input#phone(placeholder="Teléfono (opcional)" v-model="phone" inputmode="tel")
            span.has-error {{ errors[0] }}
          ValidationProvider(v-if="identityCheck" name="Cobertura" rules="required" v-slot="{ errors }")
            el-select#coverage.signup__select(v-model="coverage" placeholder="¿Qué cobertura de salud tenés?")
              el-option(v-for="c in coverageOptions" :key="c.value" :label="c.label" :value="c.value")
            span.has-error {{ errors[0] }}
          .signup__photo(v-if="identityCheck")
            label.signup__photo-label(for="dniImage") Foto del frente de tu DNI
            p.signup__photo-hint Sacala de frente, con buena luz y sin reflejos. La usamos solo para verificar tus datos: no la guardamos.
            input#dniImage.signup__photo-input(type="file" accept="image/*" capture="environment" @change="onDniImageChange")
            span.signup__photo-name(v-if="dniImageName") ✓ {{ dniImageName }}
            span.has-error(v-if="dniImageError") {{ dniImageError }}
          el-alert.signup__alert(
            v-if="mismatchMessage"
            type="warning"
            :title="mismatchMessage"
            :closable="false"
            show-icon
          )
          el-button(type="primary" native-type="submit" :loading="isLoading") Registrarme
          router-link.login__link(:to="{ name: 'login' }") ¿Ya tenés cuenta? Ingresá haciendo click aquí
      el-dialog(
        :visible.sync="residenceDialogVisible"
        title="Actualizá tu domicilio"
        width="92%"
        custom-class="signup__dialog"
        append-to-body
      )
        p.signup__dialog-text {{ residenceDialogText }}
        div(slot="footer")
          el-button(type="primary" @click="residenceDialogVisible = false") Entendido
</template>

<style lang="scss" scoped>
.login__date,
.signup__select {
  width: 100%;
  margin-bottom: 10px;
}
.signup__title {
  margin: 0 0 4px;
  font-size: 18px;
  color: $primary;
  text-align: center;
}
.signup__hint {
  margin: 0 0 16px;
  font-size: 12px;
  color: #64748b;
  text-align: center;
  line-height: 1.4;
}
.signup__cuil {
  margin: 0 0 10px;
}
.signup__cuil-label {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}
.signup__cuil-parts {
  display: flex;
  align-items: center;
  gap: 6px;
}
.signup__cuil-prefix {
  flex: 0 0 92px;
}
.signup__cuil-dni {
  flex: 1 1 auto;
}
.signup__cuil-check {
  flex: 0 0 56px;
}
.signup__cuil-sep {
  color: #94a3b8;
}
.signup__cuil-dni ::v-deep .el-input__inner,
.signup__cuil-check ::v-deep .el-input__inner {
  background: #f1f5f9;
  color: #334155;
  text-align: center;
  cursor: default;
}
.signup__cuil-hint {
  margin: 4px 0 0;
  font-size: 11px;
  color: #64748b;
  line-height: 1.4;
}
.signup__photo {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 4px 0 12px;
  padding: 10px;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
}
.signup__photo-label {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}
.signup__photo-hint {
  margin: 0;
  font-size: 11px;
  color: #64748b;
  line-height: 1.4;
}
.signup__photo-input {
  font-size: 12px;
}
.signup__photo-name {
  font-size: 12px;
  color: #15803d;
}
.signup__alert {
  margin-bottom: 12px;
}
.signup__dialog-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #334155;
}
</style>
